import { Auth0Config } from 'config/config';
import { Auth0Lock } from 'auth0-lock';

/*
 *  User Login/SignUp popup
 *  (Auth0 Lock )
 */

// Custom Sign Up Fields
const firstNameField = {
	name: "firstName",
	placeholder: "first name",
	// The following properties are optional
	icon: "http://i.cdnds.net/forum/statusicon/user_offline.png",
	validator: (value) => {
		return {
			valid: (value && value.trim().length >= 1),
			hint: "Must be at least 1 character long"
		};
	}
}

const lastNameField = {
	name: "lastName",
	placeholder: "last name",
	// The following properties are optional
	icon: "http://i.cdnds.net/forum/statusicon/user_offline.png",
	validator: (value) => {
		return {
			valid: (value && value.trim().length >= 1),
			hint: "Must be at least 1 character long"
		};
	}
}

export interface IAuthResult {
	id: string;
	// expiresIn: number;
	name: string;
	expiresAt: number;
	accessToken: string;
	idToken: string;
	// meta data info injected Auth0 rule into idToken
	metaInfo: any;
}



// Available Lock configuration options: https://auth0.com/docs/libraries/lock/v11/configuration
const lock = new Auth0Lock(Auth0Config.clientId, Auth0Config.domain, {
	allowLogin: true,
	allowSignUp: true,
	allowForgotPassword: true,
	closable: true,
	additionalSignUpFields: [firstNameField, lastNameField],
	allowedConnections: null,
	rememberLastLogin: false,
	hashCleanup: false,
	mustAcceptTerms: false,
	auth: {
		audience: Auth0Config.audience,
		redirectUrl: Auth0Config.callbackAuthenticateUrl,
		responseType: 'token id_token',
		autoParseHash: false,		// Make sure we use our own routing to process hash => see processAuthenticationFromUrlHash
		params: {
			scope: 'openid profile',
			prompt: "select_account"
		}
	},
	languageDictionary: {
		title: '',
		signUpTitle: '',
		signUpTerms: 'By clicking Sign Up, you agree to our <a href="https://fluent-forever.com/terms-of-service/" target="_blank" style="text-decoration: underline;">Terms</a> and <a href="https://fluent-forever.com/privacy-policy/" target="_blank" style="text-decoration: underline;">Privacy Policy</a>'
	},
	theme: {
		logo: 'https://fluent-forever.com/wp-content/uploads/2018/10/Fluent-Forever-Option-1-01.png',
		primaryColor: '#60c290'
	},
	prefill: { email: true, username: true },
	defaultADUsernameFromEmailPrefix: false,
	socialButtonStyle: 'small',
	configurationBaseUrl: 'https://cdn.auth0.com'
});

lock.on("authenticated", (authResult) => {
	//Auth0 authenticated handler
});

const handleLockError = (error) => {
	console.error(error);
}

lock.on("authorization_error", handleLockError);
lock.on("unrecoverable_error", handleLockError);

let tokenRenewalTimeout = null;

/*
 *  User AUTHENTICATION Management
 *  (Auth0 API Wrapper)
 */
class Auth0Authentication {

	userProfile = null;

	constructor() {
		// make sure the refresh is always scheduled (even when just page reloads)

	}

	public openLogin = async () => {
		lock.show({
			allowLogin: true,
			allowSignUp: true,
			allowForgotPassword: true,
			initialScreen: 'login'
		});
	}

	public openSignUp = async () => {
		lock.show({
			allowLogin: true,
			allowSignUp: true,
			allowForgotPassword: true,
			initialScreen: 'signUp'
		});
	}

	// Clear User session
	logout = async () => {
		localStorage.removeItem('access_token');
		localStorage.removeItem('id_token');
		localStorage.removeItem('expires_at_datetime');
		localStorage.removeItem('scopes');
		localStorage.removeItem('user_info');
		this.userProfile = null;
		clearTimeout(tokenRenewalTimeout);
	}

	/* Parse authentication callback information in hash string (callback from authentication provider redirected to url+#hash)  */
	parseAuthenticationHash = (urlHashValue) => {
		// Parses a URL hash fragment to extract the result of an Auth0 authentication response
		return new Promise<IAuthResult>((resolve, reject) => {
			lock.resumeAuth(urlHashValue, (err, authResult) => {
				if (authResult && authResult.accessToken && authResult.idToken) {
					console.debug('Authentication Success');
					const result: IAuthResult = this.toAuthResult(authResult);
					console.debug(result);
					resolve(result);
				} else {
					console.error('Authentication Failed!');
					console.error(err);
					reject(err);
				}
			});
		});
	}

	getUserProfile = (accessToken, callback) => {
		if (accessToken) {
			lock.getUserInfo(accessToken, (err, profile) => {
				if (profile) {
					this.userProfile = profile;
				}
				if (callback) {
					callback(err, profile);
				}
			});
		}
	}

	scheduleTokenRenewal = (expiresAtDate, callback: (result: IAuthResult) => void) => {
		//const expiresAtDate = JSON.parse(localStorage.getItem('expires_at_datetime'));
		if (expiresAtDate) {
			const delay = expiresAtDate - (Date.now() + 30000);	// refresh 30 seconds before expiry
			const expiryDate = new Date(expiresAtDate);
			console.debug(`Scheduling token renewal to [${expiryDate}] delay [${delay}]`)
			if (delay > 0) {
				clearTimeout(tokenRenewalTimeout);	// cancel previous refresh
				tokenRenewalTimeout = setTimeout(() => {
					this.renewToken().then((result) => {
						if (callback) {
							callback(result);
						}
					});
				}, delay);
			}
		}
	}

	/** acquire a new token for a user who is already authenticated */
	renewToken = () => {
		return new Promise<IAuthResult>((resolve, reject) => {
			lock.checkSession({},
				(err, authResult) => {
					if (authResult) {
						console.debug(`New Token received`);
						const result = this.toAuthResult(authResult);
						resolve(result);
					} else {
						const error = `Failed to refresh token. Details:${err.error}: ${err.error_description}`;
						console.error(error);
						reject(error);
					}
				}
			);
		});
	}

	private toAuthResult(authResult: any): IAuthResult {
		console.debug('Auth Result');
		console.debug(authResult);
		if (authResult) {
			const metaInfo = authResult.idTokenPayload["https://app.fluent-forever.com/user_info"] || {}
			return {
				id: authResult.idTokenPayload.sub,
				//expiresIn: authResult.expiresIn,
				expiresAt: ((authResult.expiresIn * 1000) + new Date().getTime()),
				accessToken: authResult.accessToken,
				idToken: authResult.idToken,
				name: authResult.idTokenPayload.name,
				metaInfo
			};
		}
		return null;
	}

	// /** Store Access Token & User Id */
	// private setSession(authResult) {
	// 	if (authResult) {
	// 		console.debug('Store authentication values...');
	// 		console.debug(authResult);

	// 		const expiresAt = JSON.stringify((authResult.expiresIn * 1000) + new Date().getTime());
	// 		localStorage.setItem('access_token', authResult.accessToken);
	// 		localStorage.setItem('id_token', authResult.idToken);
	// 		localStorage.setItem('expires_at_datetime', expiresAt);
	// 		localStorage.setItem('user_auth0_id', authResult.idTokenPayload.sub);

	// 		// schedule a token renewal
	// 		this.scheduleTokenRenewal(expiresAt);

	// 	} else {
	// 		console.error('Failed to store authentication result');
	// 	}
	// }
}

export const Auth0 = new Auth0Authentication()



// WEBPACK FOOTER //
// ./src/api/Auth0.ts